<template>
  <div class="weekly-sales-chart">
    <div class="chart-header">
      <h3>Weekly Sales Overview</h3>
      <div class="chart-controls">
        <button @click="refreshChart" :disabled="loading" class="btn btn-small btn-secondary">
          <span v-if="loading" class="spinner-small"></span>
          <span v-else>🔄</span>
          Refresh
        </button>
      </div>
    </div>

    <div v-if="loading" class="chart-loading">
      <div class="spinner"></div>
      <p>Loading chart data...</p>
    </div>

    <div v-else-if="error" class="chart-error">
      <p>⚠️ {{ error }}</p>
      <button @click="refreshChart" class="btn btn-primary btn-small">Try Again</button>
    </div>

    <div v-else-if="chartData" class="chart-container">
      <Bar
        :data="chartData"
        :options="chartOptions"
        class="chart-canvas"
      />
      
      <div class="chart-summary">
        <div class="summary-item">
          <span class="label">Weekly Total:</span>
          <span class="value">${{ weeklyTotal.toFixed(2) }}</span>
        </div>
        <div class="summary-item">
          <span class="label">Daily Average:</span>
          <span class="value">${{ dailyAverage.toFixed(2) }}</span>
        </div>
        <div class="summary-item">
          <span class="label">Best Day:</span>
          <span class="value">{{ bestDay }}</span>
        </div>
      </div>
    </div>

    <div v-else class="chart-empty">
      <div class="empty-icon">📊</div>
      <p>No sales data available for the chart</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js'
import api from '../services/api'

// Register Chart.js components
ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
)

// Props
const props = defineProps({
  height: {
    type: Number,
    default: 400
  },
  refreshInterval: {
    type: Number,
    default: 0 // 0 means no auto-refresh
  }
})

// Emits
const emit = defineEmits(['dataLoaded', 'error'])

// State
const loading = ref(false)
const error = ref(null)
const rawData = ref(null)

// Computed properties
const chartData = computed(() => {
  if (!rawData.value) return null

  const { weeklyData } = rawData.value
  
  return {
    labels: weeklyData.map(day => day.dayName),
    datasets: [
      {
        label: 'Daily Revenue ($)',
        data: weeklyData.map(day => day.revenue),
        backgroundColor: 'rgba(40, 167, 69, 0.8)',
        borderColor: 'rgba(40, 167, 69, 1)',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: 'Items Sold',
        data: weeklyData.map(day => day.itemsSold),
        backgroundColor: 'rgba(0, 123, 255, 0.8)',
        borderColor: 'rgba(0, 123, 255, 1)',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
        yAxisID: 'y1',
      }
    ]
  }
})

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: 'Last 7 Days Sales Performance',
      font: {
        size: 16,
        weight: 'bold'
      },
      color: '#2c3e50'
    },
    legend: {
      display: true,
      position: 'top',
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || ''
          if (label) {
            label += ': '
          }
          if (context.datasetIndex === 0) {
            // Revenue dataset
            label += '$' + context.parsed.y.toFixed(2)
          } else {
            // Items sold dataset
            label += context.parsed.y + ' items'
          }
          return label
        }
      },
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      borderColor: '#dee2e6',
      borderWidth: 1
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      },
      ticks: {
        color: '#6c757d',
        font: {
          size: 12
        }
      }
    },
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.1)'
      },
      ticks: {
        color: '#6c757d',
        font: {
          size: 12
        },
        callback: function(value) {
          return '$' + value.toFixed(0)
        }
      },
      title: {
        display: true,
        text: 'Revenue ($)',
        color: '#28a745',
        font: {
          size: 14,
          weight: 'bold'
        }
      }
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      beginAtZero: true,
      grid: {
        drawOnChartArea: false,
      },
      ticks: {
        color: '#6c757d',
        font: {
          size: 12
        },
        callback: function(value) {
          return value + ' items'
        }
      },
      title: {
        display: true,
        text: 'Items Sold',
        color: '#007bff',
        font: {
          size: 14,
          weight: 'bold'
        }
      }
    }
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false
  },
  animation: {
    duration: 1000,
    easing: 'easeInOutQuart'
  }
}))

const weeklyTotal = computed(() => {
  if (!rawData.value) return 0
  return rawData.value.summary.weeklyTotals.revenue
})

const dailyAverage = computed(() => {
  if (!rawData.value) return 0
  return rawData.value.summary.averageDaily.revenue
})

const bestDay = computed(() => {
  if (!rawData.value) return 'N/A'
  const { weeklyData } = rawData.value
  const bestDayData = weeklyData.reduce((prev, current) => 
    prev.revenue > current.revenue ? prev : current
  )
  return bestDayData.dayName
})

// Methods
const fetchChartData = async () => {
  loading.value = true
  error.value = null
  
  try {
    const response = await api.get('/dashboard/weekly-chart')
    rawData.value = response.data.data
    emit('dataLoaded', rawData.value)
  } catch (err) {
    console.error('Chart data fetch error:', err)
    error.value = err.response?.data?.error || 'Failed to load chart data'
    emit('error', error.value)
  } finally {
    loading.value = false
  }
}

const refreshChart = () => {
  fetchChartData()
}

// Auto-refresh setup
let refreshTimer = null

const setupAutoRefresh = () => {
  if (props.refreshInterval > 0) {
    refreshTimer = setInterval(() => {
      fetchChartData()
    }, props.refreshInterval * 1000)
  }
}

const clearAutoRefresh = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
}

// Lifecycle
onMounted(() => {
  fetchChartData()
  setupAutoRefresh()
})

// Cleanup
onUnmounted(() => {
  clearAutoRefresh()
})

// Expose methods for parent component
defineExpose({
  refreshChart,
  fetchChartData
})
</script>

<style scoped>
.weekly-sales-chart {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9ecef;
}

.chart-header h3 {
  color: #2c3e50;
  margin: 0;
  font-size: 1.2em;
}

.chart-controls {
  display: flex;
  gap: 10px;
}

.chart-loading,
.chart-error,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6c757d;
}

.chart-loading .spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.chart-error {
  color: #dc3545;
}

.chart-error p {
  margin-bottom: 15px;
}

.empty-icon {
  font-size: 4em;
  margin-bottom: 20px;
  opacity: 0.6;
}

.chart-container {
  position: relative;
  height: 400px;
  margin-bottom: 20px;
}

.chart-canvas {
  width: 100% !important;
  height: 100% !important;
}

.chart-summary {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 20px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.summary-item .label {
  font-size: 0.9em;
  color: #6c757d;
  font-weight: 500;
}

.summary-item .value {
  font-size: 1.2em;
  font-weight: 600;
  color: #2c3e50;
}

/* Buttons */
.btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn-small {
  padding: 4px 8px;
  font-size: 0.8em;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Responsive Design */
@media (max-width: 768px) {
  .chart-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .chart-container {
    height: 300px;
  }
  
  .chart-summary {
    flex-direction: column;
    gap: 15px;
  }
  
  .summary-item {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }
}
</style> 